import { useState, useEffect, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import Modal from "react-modal";
import Utils from "../../../common/utils";
import Core from "../../../common/clientSdk";
import { Button, CustomAgTable } from "../../../components/core_component";
import { PrimaryCta, Image, ShimmerBox, Video } from "../../../components";
import { FilterDropdown } from "../../../components";
import Icon from "../../../components/core_component/icon";
import EditInfluencerMetricsSidebar from "./EditInfluencerMetricsSidebar";
import { InstagramEmbed, YouTubeEmbed, XEmbed } from "react-social-media-embed";

const CONTENT_TYPE_MAP = {
    fxt1: "Instagram Image Post",
    ge03: "Instagram Video Post",
    utio: "Instagram Image Story",
    uyts: "Instagram Video Story",
    o5oi: "Instagram Reel",
    ttsn: "Youtube Video",
    vi82: "Youtube Shorts",
};

const PLATFORM_TYPES = [
    {
        platformName: "btll",
        label: "Instagram",
        contentTypes: ["fxt1", "ge03", "utio", "uyts", "o5oi"],
    },
    {
        platformName: "nxjo",
        label: "Youtube",
        contentTypes: ["ttsn", "vi82"],
    },
];

/**
 * InfluencersSection - Influencers table with search, filters, and pagination
 * @param {object} sectionRef - Ref for scroll navigation
 * @param {string} campaignId - Campaign ID to fetch influencer data
 * @param {function} openModal - Function to open content modal
 */
const InfluencersSection = ({ sectionRef, campaignId, openModal, onDataCount, isVisible, is_public, shareKey, isReportGenerated = true, campaignType, influencerList = [], isInitialLoading, onUpdateInfluencer, isSharedView, isPdfView }) => {
    const PAGE_SIZE = 15;
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const isLoading = isInitialLoading;
    const [totalRows, setTotalRows] = useState(null);

    useEffect(() => {
        setFilterContentType("all");
    }, [influencerList]);
    const [currentPage, setCurrentPage] = useState(1);

    // Instead of platformId, we check unique content types available in data
    const [filterContentType, setFilterContentType] = useState("all");
    const [quickFilterText, setQuickFilterText] = useState("");

    // Edit Sidebar State
    const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
    const [editingInfluencer, setEditingInfluencer] = useState(null);

    // UGC Content Preview Modal State
    const [ugcModalOpen, setUgcModalOpen] = useState(false);
    const [ugcModalItem, setUgcModalItem] = useState(null);

    const isContentCampaign = campaignType === "content";

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Flatten data: One row per content, applying filter
    const flattenedData = useMemo(() => {
        if (!influencerList || influencerList.length === 0) return [];

        const flat = [];
        influencerList.forEach((item) => {
            const deepData = item?.contents_deep_data || {};
            const contentAnalytics = item?.content_analytics || {};

            // Collect all unique content keys from both sources
            const contentKeys = new Set([
                ...Object.keys(contentAnalytics),
                ...Object.keys(deepData),
            ]);

            contentKeys.forEach((contentKey) => {
                const content = deepData[contentKey];
                const analytics = contentAnalytics[contentKey] || {};
                // If deep data missing, try to infer content_type_id from analytics or item data
                // Note: The user JSON shows content_type_id in content_analytics
                const contentTypeId =
                    content?.content_data?.content_type_id ||
                    analytics?.content_type_id;

                // Filter by content type if not "all"
                if (
                    filterContentType !== "all" &&
                    contentTypeId !== filterContentType
                ) {
                    return;
                }

                flat.push({
                    originalItem: item,
                    contentKey: contentKey,
                    contentData: content?.content_data || {},
                    analytics: analytics,
                    contentTypeId: contentTypeId,
                    influencerData: item?.influencer_data,
                    platformsData: item?.platforms_data,
                    rewardData: item?.reward_data,
                });
            });
        });
        return flat;
    }, [influencerList, filterContentType]);

    // Available Content Types for Dropdown
    const availableContentTypes = useMemo(() => {
        const types = new Set();
        influencerList.forEach((item) => {
            const deepData = item?.contents_deep_data || {};
            const contentAnalytics = item?.content_analytics || {};

            // Collect all unique content keys first
            const contentKeys = new Set([
                ...Object.keys(contentAnalytics),
                ...Object.keys(deepData),
            ]);

            contentKeys.forEach((key) => {
                const deep = deepData[key];
                const analytics = contentAnalytics[key] || {};
                const id =
                    deep?.content_data?.content_type_id ||
                    analytics?.content_type_id;
                if (id) types.add(id);
            });
        });

        const options = Array.from(types).map((id) => ({
            value: id,
            label: CONTENT_TYPE_MAP[id] || id, // Map ID to human name
        }));

        // Add "All" option at start
        options.unshift({ value: "all", label: "All Deliverables" });
        return options;
    }, [influencerList]);

    // Update parent about the data count whenever flattenedData changes
    useEffect(() => {
        if (onDataCount) {
            onDataCount(flattenedData.length);
        }
        setTotalRows(flattenedData.length);
        setCurrentPage(1);
    }, [flattenedData, onDataCount]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return flattenedData.slice(startIndex, startIndex + PAGE_SIZE);
    }, [flattenedData, currentPage]);

    const handleDownloadExcel = () => {
        if (!flattenedData || flattenedData.length === 0) {
            console.log("No data to download");
            return;
        }

        const excelData = flattenedData.map((row) => {
            // Extract analytics data
            const analytics = row?.analytics || {};
            const views = Number(analytics.view_count ?? analytics.views ?? 0);
            const likes = Number(analytics.like_count || 0);
            const comments = Number(analytics.comment_count || 0);
            const shares = Number(analytics.share || 0);
            const saves = Number(analytics.saves || 0);
            const impressions = Number(analytics.impressions || 0);
            const reach = Number(analytics.reach || 0);
            const replies = Number(analytics.replies || 0);
            const remixes = Number(analytics.remixes || 0);
            const stickerTaps = Number(analytics.sticker_taps || 0);
            const clicks = Number(analytics.total_clicks || 0);
            const conversions = Number(analytics.conversions || 0);

            // Calculate Engagement Rate (same as table)
            const er =
                views > 0
                    ? ((likes + comments + shares + saves) / views) * 100
                    : 0;

            // Calculate Total Engagements (same as table)
            const totalEngagements = likes + comments + shares + saves;

            // Get reward amount - for social campaigns, combine product_price from analytics + reward_data.value.amount
            const productPrice = Number(analytics.product_price || 0);
            const rewardValue = Number(row?.rewardData?.value?.amount || 0);
            const rewardAmount = productPrice + rewardValue;

            // Calculate CPE (same as table)
            const cpe =
                totalEngagements > 0 ? rewardAmount / totalEngagements : 0;

            // Calculate CPV (same as table)
            const cpv = views > 0 ? rewardAmount / views : 0;

            // Get platform info
            const isInsta = PLATFORM_TYPES[0].contentTypes.includes(
                row?.contentTypeId,
            );
            const isYoutube = PLATFORM_TYPES[1].contentTypes.includes(
                row?.contentTypeId,
            );
            let platformLabel = "-";
            if (isInsta) platformLabel = "Instagram";
            if (isYoutube) platformLabel = "Youtube";

            // Calculate Reach (Followers) - same as table
            let totalFollowers = 0;
            const platforms = row.platformsData || [];
            if (Array.isArray(platforms)) {
                platforms.forEach((p) => {
                    totalFollowers += Number(p.followers || 0);
                });
            } else if (platforms && typeof platforms === "object") {
                if (platforms.followers) {
                    totalFollowers = Number(platforms.followers);
                } else {
                    Object.values(platforms).forEach((p) => {
                        totalFollowers += Number(p?.followers || 0);
                    });
                }
            }

            // Get identifier/handle
            let identifier = "";
            if (Array.isArray(platforms)) {
                const targetPlatformId = isInsta ? 1 : isYoutube ? 2 : null;
                if (targetPlatformId) {
                    const platform = platforms.find(
                        (p) => p.platform_id === targetPlatformId,
                    );
                    if (platform) identifier = platform.identifier_id || "";
                }
            }
            if (!identifier) {
                identifier = platforms?.identifier_id || "";
            }

            // For UGC campaigns, get avg_views from platforms_data
            let avgViews = 0;
            if (
                isContentCampaign &&
                Array.isArray(platforms) &&
                platforms.length > 0
            ) {
                avgViews = Number(platforms[0]?.avg_views || 0);
            }

            // For UGC campaigns, return only visible columns
            if (isContentCampaign) {
                return {
                    "Influencer Name": row?.influencerData?.name || "-",
                    Handle: identifier ? `@${identifier}` : "-",
                    Deliverables: CONTENT_TYPE_MAP[row?.contentTypeId] || "-",
                    Reach: totalFollowers > 0 ? totalFollowers : "-",
                    "Average Views": avgViews > 0 ? avgViews : "-",
                    "Reward (₹)": rewardAmount > 0 ? rewardAmount : "-",
                };
            }

            // For social campaigns, return all columns
            return {
                "Influencer Name": row?.influencerData?.name || "-",
                Handle: identifier ? `@${identifier}` : "-",
                Deliverables: CONTENT_TYPE_MAP[row?.contentTypeId] || "-",
                Reach: totalFollowers > 0 ? totalFollowers : "-",
                Views: views > 0 ? views : "-",
                Likes: likes > 0 ? likes : "-",
                Comments: comments > 0 ? comments : "-",
                "E.R. (%)": er > 0 ? er.toFixed(2) : "-",
                Engagements: totalEngagements > 0 ? totalEngagements : "-",
                "CPE (₹)":
                    cpe > 0 && Number.isFinite(cpe) ? cpe.toFixed(2) : "-",
                "CPV (₹)":
                    cpv > 0 && Number.isFinite(cpv) ? cpv.toFixed(2) : "-",
                Impressions: impressions > 0 ? impressions : "-",
                "Content Reach": reach > 0 ? reach : "-",
                Shares: shares > 0 ? shares : "-",
                Saves: saves > 0 ? saves : "-",
                Replies: replies > 0 ? replies : "-",
                Remixes: remixes > 0 ? remixes : "-",
                "Sticker Taps": stickerTaps > 0 ? stickerTaps : "-",
                Clicks: clicks > 0 ? clicks : "-",
                Conversions: conversions > 0 ? conversions : "-",
                "Reward (₹)": rewardAmount > 0 ? rewardAmount : "-",
            };
        });

        const ws = XLSX.utils.json_to_sheet(excelData);

        // Adjust column widths based on campaign type
        let wscols;
        if (isContentCampaign) {
            // UGC campaigns - only 6 columns
            wscols = [
                { wch: 20 }, // Influencer Name
                { wch: 15 }, // Handle
                { wch: 25 }, // Deliverables
                { wch: 12 }, // Reach
                { wch: 15 }, // Average Views
                { wch: 12 }, // Reward
            ];
        } else {
            // Social campaigns - all columns
            wscols = [
                { wch: 20 }, // Influencer Name
                { wch: 15 }, // Handle
                { wch: 25 }, // Deliverables
                { wch: 12 }, // Reach
                { wch: 12 }, // Views
                { wch: 12 }, // Likes
                { wch: 12 }, // Comments
                { wch: 10 }, // E.R.
                { wch: 14 }, // Engagements
                { wch: 10 }, // CPE
                { wch: 10 }, // CPV
                { wch: 12 }, // Impressions
                { wch: 14 }, // Content Reach
                { wch: 10 }, // Shares
                { wch: 10 }, // Saves
                { wch: 10 }, // Replies
                { wch: 10 }, // Remixes
                { wch: 14 }, // Sticker Taps
                { wch: 10 }, // Clicks
                { wch: 12 }, // Conversions
                { wch: 12 }, // Reward
            ];
        }
        ws["!cols"] = wscols;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Influencers_Content");
        XLSX.writeFile(
            wb,
            `Influencers_Content_${campaignId || "Report"}.xlsx`,
        );
    };

    const platFormName = (row) => {
        // Determine platform from content type
        const isInsta = PLATFORM_TYPES[0].contentTypes.includes(
            row?.contentTypeId,
        );
        const isYoutube = PLATFORM_TYPES[1].contentTypes.includes(
            row?.contentTypeId,
        );

        let iconName = "";
        let identifier = "";
        let url = "";

        // Find identifier
        if (row.platformsData && Array.isArray(row.platformsData)) {
            // Priority: Match content type platform
            const targetPlatformId = isInsta ? 1 : isYoutube ? 2 : null;
            if (targetPlatformId) {
                const pData = row.platformsData.find(
                    (p) => p.platform_id === targetPlatformId,
                );
                if (pData) identifier = pData.identifier_id;
            }
        }

        // Fallback or generic
        if (!identifier) {
            identifier = row.platformsData?.identifier_id || "";
        }

        if (isInsta) {
            iconName = "instagramDiscoveryIcon";
            url = identifier ? `https://instagram.com/${identifier}` : "#";
        } else if (isYoutube) {
            iconName = "youtubeDiscoveryIcon";
            url = identifier ? `https://youtube.com/@${identifier}` : "#";
        } else {
            // Unknown platform or other
            iconName = "instagramDiscoveryIcon"; // Default
        }

        return (
            <div
                className="flex-row"
                style={{
                    marginTop: 7,
                    cursor: identifier ? "pointer" : "default",
                }}
                onClick={() => identifier && window.open(url, "_blank")}
            >
                <div className="platform-name">
                    <Icon
                        iconName={iconName}
                        height={14}
                        width={14}
                        stroke="#1977F2"
                    />
                </div>
            </div>
        );
    };

    // ============================================
    // AG Grid Column Definitions (New)
    // ============================================
    const columnDefs = useMemo(() => {
        const baseCols = [
            // Pinned Influencer Name column - stays fixed on horizontal scroll
            {
                headerName: "Influencer Name",
                field: "influencerData.name",
                minWidth: isContentCampaign ? 300 : 250,
                flex: isContentCampaign ? 2 : undefined,
                pinned: "left", // Pin to left for sticky behavior
                lockPinned: true, // Prevent unpinning
                filter: false, // No dropdown filter
                cellRenderer: (params) => {
                    const row = params.data;
                    const platformsData = row?.platformsData;
                    const identifier = Array.isArray(platformsData)
                        ? platformsData[0]?.identifier_id
                        : platformsData?.identifier_id;
                    const isInsta = PLATFORM_TYPES[0].contentTypes.includes(
                        row?.contentTypeId,
                    );
                    const isYoutube = PLATFORM_TYPES[1].contentTypes.includes(
                        row?.contentTypeId,
                    );
                    let url = "#";
                    if (identifier) {
                        if (isInsta)
                            url = `https://instagram.com/${identifier}`;
                        else if (isYoutube)
                            url = `https://youtube.com/@${identifier}`;
                    }
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 0",
                            }}
                        >
                            <Image
                                fallBack={require("../../../resources/images/fallback.png")}
                                source={
                                    row?.influencerData?.profile_picture
                                        ? Utils.generateInfluencerDpUrl(
                                              row?.influencerData
                                                  ?.influencer_id,
                                              row?.influencerData
                                                  ?.profile_picture,
                                          )
                                        : require("../../../resources/images/fallback.png")
                                }
                                className="table-profile"
                            />
                            <div>
                                <div className="influencer-table-profile">
                                    {row?.influencerData?.name || "-"}
                                </div>
                                {identifier && (
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#6B7280",
                                        }}
                                    >
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(url, "_blank");
                                            }}
                                            style={{
                                                color: "#1977F2",
                                                cursor: "pointer",
                                                fontWeight: 500,
                                            }}
                                        >
                                            @{identifier}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            // Deliverables - increased width
            {
                headerName: "Deliverables",
                field: "contentTypeId",
                minWidth: isContentCampaign ? 250 : 200,
                flex: isContentCampaign ? 1.5 : undefined,
                suppressSizeToFit: !isContentCampaign,
                hide: isMobile,
                filter: false,
                valueGetter: (params) =>
                    CONTENT_TYPE_MAP[params.data?.contentTypeId] || "-",
            },
            // Reward Amount
            {
                headerName: "Reward",
                field: "rewardData.value.amount",
                minWidth: isContentCampaign ? 180 : 130,
                flex: isContentCampaign ? 1 : undefined,
                hide: isMobile,
                filter: false,
                cellRenderer: (params) => {
                    const num = Number(params.value || 0);
                    return num <= 0 ? "-" : `₹${Utils.changeNumberFormate(num)}`;
                },
                valueFormatter: (params) => {
                    const val = params.value || 0;
                    return val <= 0
                        ? "-"
                        : `₹${Utils.changeNumberFormate(val)}`;
                },
            },
            // Reach (Followers)
            {
                headerName: "Reach",
                field: "platformsData",
                minWidth: isContentCampaign ? 180 : 120,
                flex: isContentCampaign ? 1 : undefined,
                hide: isMobile,
                filter: false,
                valueGetter: (params) => {
                    const platforms = params.data?.platformsData || [];
                    let totalFollowers = 0;
                    if (Array.isArray(platforms)) {
                        platforms.forEach((p) => {
                            totalFollowers += Number(p.followers || 0);
                        });
                    } else if (platforms && typeof platforms === "object") {
                        if (platforms.followers) {
                            totalFollowers = Number(platforms.followers);
                        } else {
                            Object.values(platforms).forEach((p) => {
                                totalFollowers += Number(p?.followers || 0);
                            });
                        }
                    }
                    return totalFollowers;
                },
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Views - For UGC campaigns, this becomes "Average Views"
            {
                headerName: isContentCampaign ? "Average Views" : "Views",
                field: isContentCampaign
                    ? "platformsData.avg_views"
                    : "analytics.view_count",
                valueGetter: (params) => {
                    if (isContentCampaign) {
                        // For UGC campaigns, get avg_views from platforms_data
                        const platforms = params.data?.platformsData || [];
                        if (Array.isArray(platforms) && platforms.length > 0) {
                            // Get avg_views from the first platform (or you could average all platforms)
                            return Number(platforms[0]?.avg_views || 0);
                        }
                        return 0;
                    } else {
                        // For social campaigns, use analytics view_count
                        return (
                            params.data?.analytics?.view_count ??
                            params.data?.analytics?.views
                        );
                    }
                },
                minWidth: isContentCampaign ? 200 : 120,
                flex: isContentCampaign ? 1.2 : undefined,
                hide: isContentCampaign ? isMobile : false, // Show for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const num = Number(params.value || 0);
                    return num <= 0 ? "-" : Utils.changeNumberFormate(num);
                },
                valueFormatter: (params) => {
                    const val = params.value || 0;
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Likes (combined for simplicity, or could be split video/static)
            {
                headerName: "Likes",
                field: "analytics.like_count",
                minWidth: 120,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const num = Number(params.value || 0);
                    return num <= 0 ? "-" : Utils.changeNumberFormate(num);
                },
                valueFormatter: (params) => {
                    const val = params.value || 0;
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Comments
            {
                headerName: "Comments",
                field: "analytics.comment_count",
                minWidth: 140,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const num = Number(params.value || 0);
                    return num <= 0 ? "-" : Utils.changeNumberFormate(num);
                },
                valueFormatter: (params) => {
                    const val = params.value || 0;
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Engagement Rate
            {
                headerName: "E.R.",
                field: "analytics.engagement_rate",
                width: 110,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                valueGetter: (params) => {
                    const analytics = params.data?.analytics || {};
                    const likes = Number(analytics.like_count || 0);
                    const comments = Number(analytics.comment_count || 0);
                    const shares = Number(analytics.share || 0);
                    const saves = Number(analytics.saves || 0);
                    const views = Number(
                        analytics.view_count ?? analytics.views ?? 0,
                    );

                    if (views > 0) {
                        return (
                            ((likes + comments + shares + saves) / views) * 100
                        );
                    }
                    return 0;
                },
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : `${val.toFixed(2)}%`;
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : `${val.toFixed(2)}%`;
                },
            },
            // Total Engagements - Sum of Likes and Comments
            {
                headerName: "Engagements",
                field: "analytics.total_engagements",
                width: 160,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                valueGetter: (params) => {
                    const likes = Number(
                        params.data?.analytics?.like_count || 0,
                    );
                    const comments = Number(
                        params.data?.analytics?.comment_count || 0,
                    );
                    const shares = Number(params.data?.analytics?.share || 0);
                    const saves = Number(params.data?.analytics?.saves || 0);
                    return (
                        likes + comments + shares + saves ||
                        params.data?.analytics?.total_engagements ||
                        0
                    );
                },
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // CPE
            {
                headerName: "CPE",
                field: "analytics.cpe",
                minWidth: 110,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                valueGetter: (params) => {
                    // For social campaigns, use product_price from content_analytics + reward_data.value.amount
                    const productPrice = Number(
                        params.data?.analytics?.product_price || 0,
                    );
                    const rewardValue = Number(
                        params.data?.rewardData?.value?.amount || 0,
                    );
                    const totalCost = productPrice + rewardValue;

                    const likes = Number(
                        params.data?.analytics?.like_count || 0,
                    );
                    const comments = Number(
                        params.data?.analytics?.comment_count || 0,
                    );
                    const shares = Number(params.data?.analytics?.share || 0);
                    const saves = Number(params.data?.analytics?.saves || 0);
                    const totalEng = likes + comments + shares + saves;

                    if (totalEng > 0) return totalCost / totalEng;
                    return 0;
                },
                cellRenderer: (params) => {
                    const num = Number(params.value);
                    if (!Number.isFinite(num) || num <= 0) return "-";
                    return `₹${num.toFixed(2)}`;
                },
                valueFormatter: (params) => {
                    const num = Number(params.value);
                    if (!Number.isFinite(num) || num <= 0) return "-";
                    return `₹${num.toFixed(2)}`;
                },
            },
            // CPV
            {
                headerName: "CPV",
                field: "analytics.cpv",
                minWidth: 110,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                valueGetter: (params) => {
                    // For social campaigns, use product_price from content_analytics + reward_data.value.amount
                    const productPrice = Number(
                        params.data?.analytics?.product_price || 0,
                    );
                    const rewardValue = Number(
                        params.data?.rewardData?.value?.amount || 0,
                    );
                    const totalCost = productPrice + rewardValue;

                    const views = Number(
                        params.data?.analytics?.view_count ||
                            params.data?.analytics?.views ||
                            0,
                    );

                    if (views > 0) return totalCost / views;
                    return 0;
                },
                cellRenderer: (params) => {
                    const num = Number(params.value);
                    if (!Number.isFinite(num) || num <= 0) return "-";
                    return `₹${num.toFixed(2)}`;
                },
                valueFormatter: (params) => {
                    const num = Number(params.value);
                    if (!Number.isFinite(num) || num <= 0) return "-";
                    return `₹${num.toFixed(2)}`;
                },
            },
            // Impressions
            {
                headerName: "Impressions",
                field: "analytics.impressions",
                width: 150,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Content Reach
            {
                headerName: "Content Reach",
                field: "analytics.reach",
                width: 120,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Shares
            {
                headerName: "Shares",
                field: "analytics.share",
                width: 130,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Saves
            {
                headerName: "Saves",
                field: "analytics.saves",
                width: 120,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Replies
            {
                headerName: "Replies",
                field: "analytics.replies",
                width: 130,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Remixes
            {
                headerName: "Remixes",
                field: "analytics.remixes",
                width: 130,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Sticker Taps
            {
                headerName: "Sticker Taps",
                field: "analytics.sticker_taps",
                width: 160,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Total Clicks
            {
                headerName: "Clicks",
                field: "analytics.total_clicks",
                width: 120,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            },
            // Conversions
            {
                headerName: "Conversions",
                field: "analytics.conversions",
                width: 160,
                hide: isContentCampaign || isMobile, // Hide for UGC campaigns
                filter: false,
                cellRenderer: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
                valueFormatter: (params) => {
                    const val = Number(params.value || 0);
                    return val <= 0 ? "-" : Utils.changeNumberFormate(val);
                },
            }, 
            // View Post Button - pinned right with Edit column
            {
                headerName: "View Post",
                field: "contentData.url",
                minWidth: isContentCampaign ? 150 : 132,
                maxWidth: isContentCampaign ? 150 : 132,
                suppressSizeToFit: true,
                filter: false,
                pinned: "right",
                cellRenderer: (params) => {
                    const data = params.data;
                    const contentUrl = data?.contentData?.url;
                    const analytics = data?.analytics || {};
                    const shortcode = analytics.shortcode;

                    // Get platform ID for URL generation
                    const platformId = Array.isArray(data.platformsData)
                        ? data.platformsData[0]?.platform_id
                        : data.platformsData?.platform_id;

                    const handleClick = () => {
                        // For UGC/content campaigns, open the modal
                        if (isContentCampaign) {
                            setUgcModalItem(data);
                            setUgcModalOpen(true);
                        } else if (contentUrl) {
                            window.open(contentUrl, "_blank");
                        } else if (shortcode) {
                            // Generate URL from shortcode
                            const normalizedId = (id) => {
                                const map = { btll: 1, yt: 2, 1: 1, 2: 2 };
                                return map[id] || id;
                            };
                            const id = normalizedId(platformId);
                            let url = "";
                            if (id === 1)
                                url = `https://www.instagram.com/p/${shortcode}/`;
                            else if (id === 2)
                                url = `https://www.youtube.com/watch?v=${shortcode}`;

                            if (url) window.open(url, "_blank");
                            else if (openModal) openModal(data?.originalItem);
                        } else if (openModal) {
                            openModal(data?.originalItem);
                        }
                    };
                    return (
                        <span
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                variant="secondary"
                                onClick={handleClick}
                                icon="eyeIcon"
                            >
                                Review
                            </Button>
                            {/* <PrimaryCta
                                invertSmall
                                iconPosition="left"
                                iconName="eyeIcon"
                                text="Review"
                                onClick={handleClick}
                            /> */}
                        </span>
                    );
                },
            },
            // Edit Button Column - only show on internal analytics page (not public/shared) AND not for UGC campaigns
            ...(!is_public && !isSharedView && !isContentCampaign
                ? [
                      {
                          headerName: "Edit",
                          field: "edit",
                          width: 70,
                          minWidth: 70,
                          maxWidth: 70,
                          suppressSizeToFit: true,
                          filter: false,
                          pinned: "right",
                          cellRenderer: (params) => {
                              return (
                                  <span
                                      style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          cursor: "pointer",
                                          padding: "8px",
                                      }}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenEditSidebar(params.data);
                                      }}
                                      title="Edit metrics"
                                  >
                                      <Icon
                                          iconName="editIcon"
                                          width={18}
                                          height={18}
                                      />
                                  </span>
                              );
                          },
                      },
                  ]
                : []),
        ];
        return baseCols;
    }, [isMobile, openModal, is_public, isContentCampaign, isSharedView]);

    // Handle opening edit sidebar for an influencer
    const handleOpenEditSidebar = useCallback((rowData) => {
        setEditingInfluencer(rowData);
        setIsEditSidebarOpen(true);
    }, []);

    // UGC Modal Handlers
    const closeUgcModal = useCallback((e) => {
        if (e) e.stopPropagation();
        setUgcModalOpen(false);
        setUgcModalItem(null);
    }, []);

    const handleDownload = useCallback((mediaId, isVideo, assignmentId) => {
        const mediaUrl = Utils.constructContentMediaUrl(assignmentId, mediaId);
        const a = document.createElement("a");
        a.href = mediaUrl;
        a.download = `downloaded_${isVideo ? "video" : "image"}.${mediaId.split(".").pop()}`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, []);

    // Render social media embed or download content for UGC modal
    const renderUgcModalContent = useCallback(
        (item) => {
            if (!item) return null;

            const contentData = item.contentData || {};
            const originalItem = item.originalItem || {};

            // Get assignment_id - try multiple paths
            const assignmentId =
                originalItem.assignment_data?.assignment_id ||
                originalItem.assignment_id ||
                item.assignmentId;

            // Get media source - use imageSource field that matches UGCContentCard
            const mediaSource = contentData.media_id || item.imageSource;
            const contentUrl = contentData.url;
            const contentTypeId = item.contentTypeId;
            const platform = Utils.socialMediaPlatform(contentTypeId);
            const isVideo = mediaSource?.split(".")?.pop() === "mp4";

            return (
                <>
                    {contentUrl && (
                        <div
                            className="embed-container"
                            style={{ width: "100%" }}
                        >
                            {platform === "Instagram" && (
                                <InstagramEmbed url={contentUrl} width="100%" />
                            )}
                            {platform === "youtube" && (
                                <YouTubeEmbed
                                    url={contentUrl}
                                    width="100%"
                                    height={620}
                                />
                            )}
                            {platform === "twitter" && (
                                <XEmbed url={contentUrl} width="100%" />
                            )}
                        </div>
                    )}
                    {mediaSource && isVideo && !contentUrl && (
                        <div
                            className="download-content"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                                alignItems: "center",
                            }}
                        >
                            <Video
                                source={Utils.constructContentMediaUrl(
                                    assignmentId,
                                    mediaSource,
                                )}
                                width="100%"
                                height={420}
                                className="content-media"
                            />
                            <PrimaryCta
                                fifth
                                onClick={() =>
                                    handleDownload(
                                        mediaSource,
                                        true,
                                        assignmentId,
                                    )
                                }
                                fontSize="font_size_large"
                                text="Download"
                                className="blue-button"
                            />
                        </div>
                    )}
                    {mediaSource && !isVideo && !contentUrl && (
                        <div
                            className="download-content"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                                alignItems: "center",
                            }}
                        >
                            <Image
                                source={Utils.constructContentMediaUrl(
                                    assignmentId,
                                    mediaSource,
                                )}
                                width="100%"
                                height={420}
                                className="content-media"
                                style={{
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                }}
                            />
                            <PrimaryCta
                                fifth
                                onClick={() =>
                                    handleDownload(
                                        mediaSource,
                                        false,
                                        assignmentId,
                                    )
                                }
                                fontSize="font_size_large"
                                text="Download"
                                className="blue-button"
                            />
                        </div>
                    )}
                    {!contentUrl && !mediaSource && (
                        <div
                            style={{
                                padding: "40px",
                                textAlign: "center",
                                color: "#6B7280",
                            }}
                        >
                            <p>No content available for preview</p>
                        </div>
                    )}
                </>
            );
        },
        [handleDownload],
    );

    // ============================================
    // Old tableSchema and customStyle (Commented out for reference)
    // ============================================
    /*
    const tableSchema = [
        {
            name: "Influencer Name",
            minWidth: "200px",
            grow: 1,
            selector: (row) => (...),
        },
        // ... other columns
    ];

    const customStyle = {
        headRow: { style: { backgroundColor: "var(--general-secondary-color)", ... } },
        // ... other styles
    };
    */

    // Shimmer Loading UI Component - Matches table structure
    const ShimmerLoading = () => (
        <div
            className="influencers-shimmer-container"
            style={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                border: "1px solid #e6e7e8",
                borderRadius: "8px",
            }}
        >
            {/* Table Header Shimmer */}
            <div
                className="shimmer-table-header"
                style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                        ? "1fr 80px 80px"
                        : "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr 120px",
                    gap: "16px",
                    padding: "16px 20px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #e6e7e8",
                    alignItems: "center",
                }}
            >
                <ShimmerBox width="100px" height="16px" />
                {!isMobile && <ShimmerBox width="100px" height="16px" />}
                <ShimmerBox width="60px" height="16px" />
                {!isMobile && <ShimmerBox width="60px" height="16px" />}
                {!isMobile && <ShimmerBox width="70px" height="16px" />}
                {!isMobile && <ShimmerBox width="50px" height="16px" />}
                {!isMobile && <ShimmerBox width="50px" height="16px" />}
                <ShimmerBox width="80px" height="16px" />
            </div>

            {/* Table Rows Shimmer */}
            {[1, 2, 3, 4, 5].map((index) => (
                <div
                    key={index}
                    className="shimmer-table-row"
                    style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                            ? "1fr 80px 80px"
                            : "2fr 1.5fr 1fr 1fr 1fr 1fr 1fr 120px",
                        gap: "16px",
                        padding: "16px 20px",
                        alignItems: "center",
                        borderBottom: index < 5 ? "1px solid #f3f4f6" : "none",
                    }}
                >
                    {/* Influencer Column - Profile + Name */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <ShimmerBox
                            width="40px"
                            height="40px"
                            borderRadius="50%"
                        />
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                            }}
                        >
                            <ShimmerBox width="100px" height="14px" />
                            <ShimmerBox width="70px" height="12px" />
                        </div>
                    </div>
                    {/* Deliverables */}
                    {!isMobile && <ShimmerBox width="120px" height="14px" />}
                    {/* Views */}
                    <ShimmerBox width="50px" height="14px" />
                    {/* Likes */}
                    {!isMobile && <ShimmerBox width="45px" height="14px" />}
                    {/* Comments */}
                    {!isMobile && <ShimmerBox width="45px" height="14px" />}
                    {/* CPE */}
                    {!isMobile && <ShimmerBox width="50px" height="14px" />}
                    {/* CPV */}
                    {!isMobile && <ShimmerBox width="50px" height="14px" />}
                    {/* View Post Button */}
                    <ShimmerBox width="80px" height="32px" borderRadius="6px" />
                </div>
            ))}
        </div>
    );

    return (
        <div className={`influencers-tab section-content ${isPdfView ? 'pdf-view' : ''}`} ref={sectionRef}>
            <div className="section-header" data-html2canvas-ignore={isPdfView ? "true" : "false"}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h3>Influencers</h3>
                    {/* Search Input for Influencer Name - positioned next to title */}
                    {!isPdfView && (
                        <div style={{ position: 'relative', width: '250px' }}>
                            <input
                                type="text"
                                placeholder="Search influencer name..."
                                value={quickFilterText}
                                onChange={(e) => setQuickFilterText(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 14px 8px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--gray-gray-50, #e6e7e8)',
                                    fontSize: '14px',
                                    outline: 'none',
                                    backgroundColor: 'var(--general-white-color, #ffffff)',
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="section-controls">
                    {!is_public && !isSharedView && isVisible !== undefined && !isPdfView && (
                        <div className='sharing-status-container'>
                            <span className="sharing-status">Sharing status</span>
                            {isVisible ? (
                                <span className="visible-badge">Visible</span>
                            ) : (
                                <span className="hidden-badge">Hidden</span>
                            )}
                        </div>
                    )}
                    {!isPdfView && (
                        <>
                            <div>
                                <FilterDropdown
                                    label={null}
                                    value={
                                        filterContentType !== 'all'
                                            ? availableContentTypes.filter((opt) => opt.value === filterContentType)
                                            : [availableContentTypes[0]]
                                    }
                                    onChange={(val) => {
                                        const selected = Array.isArray(val) && val[0] ? val[0].value : "all";
                                        setFilterContentType(selected);
                                    }}
                                    options={availableContentTypes}
                                    inputType="singleselect"
                                />
                            </div>
                            <PrimaryCta
                                text="Download Excel"
                                onClick={() => handleDownloadExcel()}
                                invertSmall
                                iconName="excelExportIcon"
                                iconPosition="left"
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="">
                {!isReportGenerated ? (
                    <div
                        style={{
                            padding: "40px",
                            textAlign: "center",
                            borderRadius: "12px",
                            minHeight: "200px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "1px dashed #e5e7eb",
                            margin: "20px 0",
                        }}
                    >
                        <h3
                            style={{
                                fontSize: "16px",
                                color: "#6B7280",
                                fontWeight: 600,
                            }}
                        >
                            Report not generated yet
                        </h3>
                        <p
                            style={{
                                color: "#9CA3AF",
                                marginTop: "8px",
                                fontSize: "14px",
                            }}
                        >
                            Click 'Generate Report' to view this data.
                        </p>
                    </div>
                ) : isLoading ? (
                    <ShimmerLoading />
                ) : (
                    <CustomAgTable
                        rowData={paginatedData || []}
                        columnDefs={columnDefs}
                        paginationPageSize={PAGE_SIZE}
                        serverSidePagination={true}
                        onPageChange={(page) => setCurrentPage(page)}
                        totalRows={totalRows}
                        currentPage={currentPage}
                        loading={false}
                        height={"auto"}
                        noDataMessage="No content found"
                        enableFilter={false}
                        enableSort={false}
                        enableFloatingFilter={false}
                        quickFilterText={quickFilterText}
                        defaultColDef={{
                            filter: false,
                            resizable: true,
                            flex: 0, // Disable flex to respect width/minWidth
                            autoHeight: false,
                        }}
                        onRowClicked={(row) => {
                            // Optional: handle row click if needed
                        }}
                    />
                )}
            </div>

            {/* Edit Influencer Metrics Sidebar */}
            <EditInfluencerMetricsSidebar
                isOpen={isEditSidebarOpen}
                onClose={() => {
                    setIsEditSidebarOpen(false);
                    setEditingInfluencer(null);
                }}
                influencer={editingInfluencer}
                campaignId={campaignId}
                onSaveSuccess={(response) => {
                    // Refresh data here using the local update logic
                    if (onUpdateInfluencer) {
                        onUpdateInfluencer(response);
                    }
                }}
                onOptimisticUpdate={(data) => {
                    if (onUpdateInfluencer) {
                        onUpdateInfluencer(data);
                    }
                }}
            />

            {/* UGC Content Preview Modal */}
            <Modal
                isOpen={ugcModalOpen}
                onRequestClose={closeUgcModal}
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        background: "var(--general-secondary-color)",
                        textAlign: "center",
                        zIndex: "5",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "90vh",
                        borderRadius: 12,
                        overflowY: "auto",
                        width: "400px",
                        minHeight: "300px",
                        padding: "20px",
                    },
                    overlay: {
                        background: "rgba(0, 0, 0, 0.95)",
                        zIndex: 1000,
                    },
                }}
            >
                <div
                    onClick={closeUgcModal}
                    style={{
                        position: "absolute",
                        right: 10,
                        top: 10,
                        cursor: "pointer",
                        zIndex: 10,
                    }}
                >
                    <Icon iconName="crossIcon" />
                </div>
                {ugcModalItem && renderUgcModalContent(ugcModalItem)}
            </Modal>
        </div>
    );
};

export default InfluencersSection;
